import { useAuth } from "react-oidc-context";
import { useState, useContext } from "react";
import { UIContext } from "../UIContext";
import {
	Button,
	Tooltip,
	Label,
	TextInput,
	Accordion,
	Badge,
} from "flowbite-react";

export function GenContract(props) {
	const auth = props.auth;
	const playerData = props.playerData;
	const ctx = useContext(UIContext);

	const getNewContract = (e) => {
		e.preventDefault();
		ctx.setLoading(true);
		fetch("/api/player/contract", {
			method: "POST",
			headers: {
				Authorization: `Bearer ${auth.user.access_token}`,
			},
		})
			.then((resp) => {
				ctx.setLoading(false);
				if (!resp.ok) {
					throw new Error("Could not generate new contract");
				}
				return resp.json();
			})
			.then((r) => {
				window.location.reload();
			})
			.catch((error) => {
				console.log(error);
				ctx.setAlert({
					type: "Error",
					title: "Contract",
					message: `Could not generate new contract. Please contact NILO support at support@nilo.win`,
					open: true,
				});
			});
	};

	return (
		<div>
			<Button color="gray" className="mx-auto text-sm" onClick={getNewContract}>
				Generate Contract
			</Button>
		</div>
	);
}

const TransferLink = (props) => {
	const contract = props.contract;
	const stripe_id = props.stripe_id;
	const auth = props.auth;
	const ctx = useContext(UIContext);

	/*
	 * Function to call transfer funds API
	 */
	const transferContractFunds = (e) => {
		e.preventDefault();

		ctx.setLoading(true);
		// The target is a span. The button is the parent element
		const button = e.target.parentElement;
		button.setAttribute("disabled", "");

		fetch("/api/stripe/transfer", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${auth.user.access_token}`,
			},
			body: JSON.stringify({ contract_id: contract.id }),
		})
			.then((resp) => {
				ctx.setLoading(false);
				if (!resp.ok) {
					ctx.setAlert({
						open: true,
						type: "Error",
						title: "Transfer",
						message:
							"Could not transfer funds. Please try again later. <br /> \
            If this error persists please contact support at <br /> \
            <a href='mailto:support@nilo.win'><b>support@nilo.win</b></a>",
					});
				}
			})
			.catch((error) => {
				console.error(error);
				ctx.setAlert({
					open: true,
					type: "Error",
					title: "Transfer",
					message: error.message,
				});
			});
	};

	if (contract.contract_state === "Fulfilled") {
		/*
		 * Check if money from the sponsor is available
		 * Enable Funds Transfer Only if contract state is fulfilled,
		 * and if money is available
		 */

		const a = new Date(contract.available_on + "+00:00");
		if (a > Date.now()) {
			return (
				<div className="my-4">
					<Tooltip
						content={`This Contract will be eligible for payout after ${a.toLocaleString()}`}
					>
						<Button size="sm" className="disabled:opacity-50" disabled>
							Transfer Money
						</Button>
					</Tooltip>
				</div>
			);
		}
		return (
			<div>
				{stripe_id !== null ? (
					<div className="my-4">
						<Button
							size="sm"
							onClick={transferContractFunds}
							className="disabled:opacity-50"
						>
							Transfer Money
						</Button>
					</div>
				) : (
					<div>Setup your Stripe Account</div>
				)}
			</div>
		);
	}
	return <></>;
};

// Social Media Verification Link submission form
const VerificationForm = (props) => {
	const contract = props.contract;
	const auth = props.auth;
	const ctx = useContext(UIContext);
	const [editMode, setEditMode] = props.edit;
	//
	// If Contract is already verified, return just the URL
	if (
		contract.contract_state === "Fulfilled" ||
		contract.contract_state === "Completed"
	) {
		return (
			<div>
				<div>
					<a onClick={() => window.open(contract.verification_url)}>
						<span className="text-cyan-600 underline cursor-pointer">
							Verification URL: {contract.verification_url}
						</span>
					</a>
				</div>
			</div>
		);
	}

	// Function to submit Social Media Post URL for Verification
	const submitVerification = (e) => {
		e.preventDefault();
		ctx.setLoading(true);
		let url = document.getElementById("verification_url").value;
		fetch(`/api/contract/verification`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${auth.user.access_token}`,
			},
			body: JSON.stringify({ contract_id: contract.id, url: url }),
		})
			.then((resp) => {
				ctx.setLoading(false);
				setEditMode(false);
			})
			.then(() => window.location.reload());
	};

	return (
		<div>
			{editMode === true ? (
				<form onSubmit={(e) => submitVerification(e, contract.id)}>
					<div>
						<Label htmlFor="verification_url" value="Verification URL" />
						<TextInput
							id="verification_url"
							type="text"
							required
							placeholder={
								contract.verification_url === null
									? `https://example.com/social/media/post`
									: contract.verification_url
							}
							helperText={
								<p>
									Enter the URL for the Social Media post. Please make sure to
									allow NILO to access this post
								</p>
							}
						/>
						<div className="flex w-full">
							<Button type="submit">Submit</Button>
							{contract.verification_url === null ? (
								<></>
							) : (
								<Button color="Gray" onClick={() => setEditMode(false)}>
									Cancel
								</Button>
							)}
						</div>
					</div>
				</form>
			) : (
				<div>
					Verification URL
					<div>
						<a onClick={() => window.open(contract.verification_url)}>
							<span className="text-cyan-600 underline cursor-pointer">
								{contract.verification_url}
							</span>
						</a>
					</div>
					<Button onClick={() => setEditMode(true)}>Edit</Button>
				</div>
			)}
		</div>
	);
};

// Info about each contract, along with the form to submit
// social media verification links
const ContractInfo = (contract, auth, stripe_id) => {
	const [editMode, setEditMode] = useState(
		contract.verification_state !== "Submitted"
	);
	const cdatefmt = Intl.DateTimeFormat("en-US", { dateStyle: "medium" });
	const USD = Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	});
	let color = "warning";
	switch (contract.contract_state) {
		case "Signed":
			color = "info";
			break;
		case "Fulfilled":
			color = "success";
			break;
		case "Completed":
			color = "failure";
			break;
		default:
			break;
	}

	return (
		<Accordion.Panel>
			<Accordion.Title>
				<div className="flex flex-wrap gap-2 justify-between">
					<div>
						<p>{cdatefmt.format(Date.parse(contract.contract_date))}</p>
					</div>
					<div>
						<Badge color={color} size="sm">
							{contract.contract_state}
						</Badge>
					</div>
				</div>
			</Accordion.Title>
			<Accordion.Content>
				<div className="grid-flow-col">
					<div>
						<p>Contract ID: {contract.id}</p>
					</div>
					<div className="border-none rounded-md bg-slate-50">
						<p>
							Contract Link:{" "}
							<div>
								<a onClick={() => window.open(contract.contract_api_url)}>
									<span className="text-cyan-600 underline cursor-pointer">
										{contract.contract_api_url}
									</span>
								</a>
							</div>
						</p>
					</div>
					<div>
						Contract Amount:{" "}
						<span className="font-bold">
							{USD.format(contract.amount / 100)}
						</span>
					</div>
					<div className="border-none rounded-md bg-slate-50">
						<VerificationForm
							contract={contract}
							auth={auth}
							edit={[editMode, setEditMode]}
						/>
					</div>
					<div>
						<TransferLink
							contract={contract}
							stripe_id={stripe_id}
							auth={auth}
						/>
					</div>
				</div>
			</Accordion.Content>
		</Accordion.Panel>
	);
};

// List of Contracts for a player
export function ListContracts(props) {
	const auth = props.auth;
	const playerData = props.playerData;
	playerData.contracts.sort((a, b) => b.id - a.id);

	return (
		<>
			{playerData.contracts.length > 0 ? (
				<div className="my-4">
					<Accordion collapseAll>
						{playerData.contracts.map((contract) => {
							return ContractInfo(contract, auth, playerData.stripe_id);
						})}
					</Accordion>
				</div>
			) : (
				<></>
			)}
		</>
	);
}
